//type
import type { ComponentProps } from 'react';

import clsx from 'clsx';

import type { SocialProfileType } from '@/types/socialProfileType';

// Lib
import { getErrorWarningMessageToken } from '@/lib/socialprofiles/status';

//component
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';

const textSizes = {
    xs: {
        slug: 'text-xs',
        name: 'text-xxs',
        gap: 'gap-0.75'
    },
    sm: {
        slug: 'text-sm',
        name: 'text-xs',
        gap: 'gap-0.75'
    }
};

export type SocialProfileInfoProps = Partial<
    Pick<ComponentProps<typeof Avatar>, 'size' | 'error' | 'warning'>
> & {
    profile: SocialProfileType;
    textSize?: keyof typeof textSizes;
} & (
        | {
              displayErrorWarning?: true;
              isAdmin: boolean;
          }
        | {
              displayErrorWarning?: false;
              isAdmin?: boolean;
          }
    );

export function SocialProfileInfo({
    profile,
    textSize = 'xs',
    displayErrorWarning = false,
    isAdmin = false,
    ...restProps
}: SocialProfileInfoProps) {
    const textSizeItem = textSizes[textSize];
    const errorMessage = displayErrorWarning
        ? getErrorWarningMessageToken({ socialProfile: profile, isAdmin })
        : null;
    return (
        <>
            <Avatar
                fallback={profile?.slug?.[0]}
                url={profile?.external_api_data?.avatar}
                profileType={profile?.social_network_type}
                error={displayErrorWarning ? profile?.auth_status.type === 'error' : false}
                warning={displayErrorWarning ? profile?.auth_status.type === 'warning' : false}
                {...restProps}
            />
            <div
                className={clsx(
                    'flex flex-1 flex-col overflow-hidden text-left',
                    textSizeItem.gap
                )}>
                <p className={clsx('truncate', textSizeItem.slug)}>{profile?.slug}</p>
                <p
                    className={clsx(
                        'truncate font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf',
                        textSizeItem.name
                    )}>
                    {profile?.external_api_data?.name}
                </p>
                {displayErrorWarning &&
                (profile?.auth_status.type === 'error' ||
                    profile?.auth_status.type === 'warning') ? (
                    <p
                        className={clsx('text-xxs', {
                            'text-icoOrange': profile.auth_status.type === 'warning',
                            'text-icoRed': profile.auth_status.type === 'error'
                        })}>
                        {errorMessage}
                    </p>
                ) : null}
            </div>
        </>
    );
}
